import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { platePath } from 'src/app/utils/canonical-url';
import { matchRegistration } from 'src/app/utils/registration-match';
import { classifyRegistration } from 'src/app/components/classification-pill/format-token';

interface SearchTab {
  id: string;
  label: string;
  /** Route for tab-specific search pages; null = free-text search. */
  route: string | null;
}

/**
 * Shared homepage search (frozen design: tabs + single input + Search).
 * Used by both the public hero and the member briefing header, per the
 * one-composition rule. Tabs are keyboard-accessible (arrow keys move,
 * tab list semantics). Free-text goes to /search-results; the Initials,
 * Names and Dateless tabs open their register pages with the term applied.
 * Build your own returns once its redesigned page ships.
 */
@Component({
  selector: 'pla-home-search',
  templateUrl: './home-search.component.html',
  styleUrls: ['./home-search.component.scss'],
})
export class HomeSearchComponent {
  @Input() public placeholder = 'Registration, initials or name';

  public tabs: SearchTab[] = [
    { id: 'any', label: 'Any type', route: null },
    { id: 'initials', label: 'Initials', route: '/plates/initials' },
    { id: 'names', label: 'Names', route: '/plates/name-plates' },
    { id: 'dateless', label: 'Dateless', route: '/plates/dateless' },
  ];
  public activeTab = this.tabs[0];
  public term = '';

  constructor(private router: Router) {}

  public selectTab(tab: SearchTab): void {
    this.activeTab = tab;
  }

  public onTabKeydown(event: KeyboardEvent, index: number): void {
    let next = -1;
    if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
    if (event.key === 'ArrowLeft')
      next = (index - 1 + this.tabs.length) % this.tabs.length;
    if (next >= 0) {
      event.preventDefault();
      this.activeTab = this.tabs[next];
      const el = document.getElementById(`hm-search-tab-${this.tabs[next].id}`);
      el?.focus();
    }
  }

  /**
   * The term as one registration ("XJ 220") when it reads as one, so the box
   * can offer the plate's own page next to the broad search. Free-text tab only.
   */
  public get plateMatch(): string | null {
    return this.activeTab.route ? null : matchRegistration(this.term);
  }

  public goToPlate(): void {
    const reg = this.plateMatch;
    if (reg) this.router.navigateByUrl(platePath(reg));
  }

  public submit(): void {
    const term = this.term.trim();
    if (this.activeTab.route && !term) {
      this.router.navigate([this.activeTab.route]);
      return;
    }
    if (!term) return;
    if (this.activeTab.id === 'initials') {
      const letters = term.replace(/[^A-Za-z]/g, '').toUpperCase();
      // Initials are one to three letters (the page's own token rule).
      this.router.navigate(letters.length >= 1 && letters.length <= 3 ? ['/plates/initials', letters] : ['/plates/initials']);
      return;
    }
    if (this.activeTab.id === 'names') {
      this.router.navigate(['/plates/name-plates'], { queryParams: { name: term } });
      return;
    }
    if (this.activeTab.id === 'dateless') {
      // A dateless mark opens its own format; anything else searches the
      // page's default format, as the page's own search box does.
      const format = classifyRegistration(term);
      this.router.navigate(['/plates/dateless', format ? format.token : '1x2'], { queryParams: { q: term } });
      return;
    }
    this.router.navigate(['/search-results', term]);
  }
}
